import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowTopRightOnSquareIcon, BookmarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid'
import { saveArticleFromSearch } from '../../Requests/ArticleBibliographyRequests'
import { getTeamUuid } from '../../utils/cookies'
import { sanitizeUserInput } from '../../utils/security'
import SuccessPopup from '../PopupNotifications/SuccessPopup'
import ErrorPopup from '../PopupNotifications/ErrorPopup'

function SearchResultsList({ currentSearch, results, projectId, onArticleSaved, loading, isHistoryView = false }) {
    const { t } = useTranslation()
    const [savingArticles, setSavingArticles] = useState(new Set())
    const [savedArticles, setSavedArticles] = useState(new Set())
    const [successMessage, setSuccessMessage] = useState('')
    const [errorMessage, setErrorMessage] = useState('')

    const handleSaveArticle = async (article) => {
        try {
            setSavingArticles(prev => new Set([...prev, article.id]))
            
            await saveArticleFromSearch({
                article_search_result_id: article.id,
                team_uuid: getTeamUuid(),
                project_id: projectId
            })

            setSavedArticles(prev => new Set([...prev, article.id]))
            setSuccessMessage(t('articleBibliography.search.saveSuccess', { title: article.title }))
            onArticleSaved()
        } catch (error) {
            console.error('Error saving article:', error)
            if (error.response?.data?.message === 'Article already saved') {
                setSavedArticles(prev => new Set([...prev, article.id]))
                setErrorMessage(t('articleBibliography.errors.alreadySaved'))
            } else {
                setErrorMessage(error.response?.data?.message || t('articleBibliography.errors.saveFailed'))
            }
        } finally {
            setSavingArticles(prev => {
                const newSet = new Set(prev)
                newSet.delete(article.id)
                return newSet
            })
        }
    }

    const openArticle = (url) => {
        if (url) {
            window.open(sanitizeUserInput(url), '_blank', 'noopener,noreferrer')
        }
    }

    const formatAuthors = (authors) => {
        if (!authors || authors.length === 0) return t('common.unknown')
        
        const authorNames = authors.slice(0, 3).map(author => sanitizeUserInput(author.name || author))
        if (authors.length > 3) {
            return `${authorNames.join(', ')} et al.`
        }
        return authorNames.join(', ')
    }

    if (loading) {
        return (
            <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
                    <span className="text-gray-600">{t('articleBibliography.search.searching')}</span>
                </div>
                <p className="text-sm text-gray-500 text-center mt-2">
                    {t('articleBibliography.search.searchingDescription')}
                </p>
            </div>
        )
    }

    if (!currentSearch && results.length === 0) {
        return null
    }

    return (
        <>
            {!isHistoryView && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="flex items-center space-x-2 mb-2">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-500" />
                        <h3 className="text-lg font-medium text-gray-900">
                            {t('articleBibliography.search.results')}
                        </h3>
                    </div>
                    {currentSearch && (
                        <div className="text-sm text-gray-600 space-y-1">
                            <p>
                                <span className="font-medium">{t('articleBibliography.search.query')}:</span> 
                                {sanitizeUserInput(currentSearch.search_query.replace(/^"|"$/g, ''))}
                            </p>
                            <p>
                                <span className="font-medium">{t('articleBibliography.search.totalResults')}:</span> 
                                {currentSearch.total_results ? currentSearch.total_results.toLocaleString() : t('common.unknown')}
                            </p>
                            <p className="text-xs text-gray-500">
                                {t('articleBibliography.search.completedAt')}: {new Date(currentSearch.completed_at).toLocaleString()}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {results.length === 0 ? (
                <div className="text-center py-8">
                    <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">
                        {t('articleBibliography.search.noResults')}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {t('articleBibliography.search.noResultsDescription')}
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {results.map((article) => (
                        <div key={article.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start">
                                <div className="flex-1 min-w-0 mr-4">
                                    <h4 className="text-lg font-medium text-gray-900 line-clamp-2 mb-2">
                                        {sanitizeUserInput(article.title)}
                                    </h4>
                                    
                                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                                        <span>
                                            <strong>{t('articleBibliography.savedArticles.table.authors')}:</strong> {formatAuthors(article.authors)}
                                        </span>
                                        {article.publication_year && (
                                            <span>
                                                <strong>{t('articleBibliography.savedArticles.table.year')}:</strong> {article.publication_year}
                                            </span>
                                        )}
                                        {article.cited_by_count !== null && (
                                            <span>
                                                <strong>{t('articleBibliography.savedArticles.table.citations')}:</strong> {article.cited_by_count.toLocaleString()}
                                            </span>
                                        )}
                                    </div>

                                    {article.journal_name && (
                                        <p className="text-sm text-gray-600 mb-2">
                                            <strong>{t('articleBibliography.savedArticles.table.journal')}:</strong> {sanitizeUserInput(article.journal_name)}
                                        </p>
                                    )}

                                    {article.abstract && (
                                        <p className="text-sm text-gray-700 mb-3">
                                            {sanitizeUserInput(article.abstract)}
                                        </p>
                                    )}

                                    {article.doi && (
                                        <p className="text-xs text-gray-500">
                                            <strong>DOI:</strong> {sanitizeUserInput(article.doi)}
                                        </p>
                                    )}
                                </div>

                                <div className="flex flex-col space-y-2 flex-shrink-0">
                                    {(article.url || (article.locations && article.locations[0]?.landing_page_url)) && (
                                        <button
                                            onClick={() => openArticle(article.url || article.locations[0]?.landing_page_url)}
                                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
                                            title={t('articleBibliography.search.openArticle')}
                                        >
                                            <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
                                            {t('articleBibliography.search.open')}
                                        </button>
                                    )}
                                    
                                    <button
                                        onClick={() => handleSaveArticle(article)}
                                        disabled={savingArticles.has(article.id) || savedArticles.has(article.id)}
                                        className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50 disabled:cursor-not-allowed ${
                                            savedArticles.has(article.id)
                                                ? 'border-green-300 text-green-700 bg-green-50'
                                                : 'border-black text-white bg-black hover:bg-gray-800'
                                        }`}
                                        title={t('articleBibliography.search.saveToProject')}
                                    >
                                        {savingArticles.has(article.id) ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-1"></div>
                                                {t('articleBibliography.search.saving')}
                                            </>
                                        ) : savedArticles.has(article.id) ? (
                                            <>
                                                <BookmarkSolidIcon className="h-4 w-4 mr-1" />
                                                {t('articleBibliography.search.saved')}
                                            </>
                                        ) : (
                                            <>
                                                <BookmarkIcon className="h-4 w-4 mr-1" />
                                                {t('articleBibliography.search.save')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {successMessage && (
                <SuccessPopup
                    text={successMessage}
                    handleClose={() => setSuccessMessage('')}
                />
            )}

            {errorMessage && (
                <ErrorPopup
                    text={errorMessage}
                    handleClose={() => setErrorMessage('')}
                />
            )}
        </>
    )
}

export default SearchResultsList